export interface WaveGroup {
  enemy: string;
  count: number;
  delay: number;
  offset?: number;
  lane?: number;
  /** Optional authored formation; editor/dev tuning can override squad size. */
  formation?: 'column' | 'line';
}

export interface WaveDef {
  id: number;
  groups: WaveGroup[];
  earlyBonus: number;
  label?: string;
}

const g = (
  enemy: string,
  count: number,
  delay: number,
  offset?: number,
  lane?: number,
): WaveGroup => ({ enemy, count, delay, offset, lane });

type Theme =
  | 'kappa'
  | 'helios'
  | 'cinder'
  | 'meridian'
  | 'ashfall'
  | 'theta'
  | 'obsidian'
  | 'cryostation'
  | 'floodway'
  | 'verdigris'
  | 'umbra'
  | 'aurora'
  | 'emberfall'
  | 'tideline';

/** Late-game reinforcement pools per theatre. */
const FAMILY_POOL: Record<Theme, { enemy: string; count: number; delay: number }[]> = {
  kappa: [
    { enemy: 'husk', count: 10, delay: 0.7 },
    { enemy: 'locust', count: 16, delay: 0.25 },
    { enemy: 'wasp', count: 8, delay: 0.4 },
    { enemy: 'dronebot', count: 6, delay: 0.6 },
    { enemy: 'plateback', count: 4, delay: 1.2 },
    { enemy: 'goliath', count: 2, delay: 2.0 },
  ],
  helios: [
    { enemy: 'dronebot', count: 8, delay: 0.5 },
    { enemy: 'sentrybot', count: 3, delay: 1.5 },
    { enemy: 'veilguard', count: 5, delay: 1.0 },
    { enemy: 'dashling', count: 12, delay: 0.35 },
    { enemy: 'haulerbot', count: 2, delay: 2.0 },
    { enemy: 'archon', count: 1, delay: 1.0 },
  ],
  cinder: [
    { enemy: 'locust', count: 22, delay: 0.2 },
    { enemy: 'wasp', count: 10, delay: 0.35 },
    { enemy: 'venomspitter', count: 4, delay: 1.0 },
    { enemy: 'hivecrawler', count: 2, delay: 2.0 },
    { enemy: 'plateback', count: 4, delay: 1.2 },
    { enemy: 'goliath', count: 3, delay: 1.8 },
  ],
  meridian: [
    { enemy: 'gunner', count: 6, delay: 0.8 },
    { enemy: 'raider', count: 5, delay: 0.9 },
    { enemy: 'sharpshooter', count: 4, delay: 1.0 },
    { enemy: 'sentrybot', count: 3, delay: 1.5 },
    { enemy: 'nulltick', count: 3, delay: 1.1 },
    { enemy: 'archon', count: 2, delay: 1.2 },
  ],
  ashfall: [
    { enemy: 'raider', count: 6, delay: 0.8 },
    { enemy: 'gunner', count: 6, delay: 0.7 },
    { enemy: 'haulerbot', count: 3, delay: 1.8 },
    { enemy: 'reaperbot', count: 1, delay: 1.5 },
    { enemy: 'plateback', count: 5, delay: 1.1 },
    { enemy: 'goliath', count: 3, delay: 1.7 },
  ],
  theta: [
    { enemy: 'mechpilot', count: 1, delay: 1.5 },
    { enemy: 'reaperbot', count: 1, delay: 1.5 },
    { enemy: 'sentrybot', count: 3, delay: 1.4 },
    { enemy: 'venomspitter', count: 4, delay: 1.0 },
    { enemy: 'archon', count: 2, delay: 1.2 },
    { enemy: 'goliath', count: 3, delay: 1.6 },
  ],
  obsidian: [
    { enemy: 'plateback', count: 5, delay: 1.1 },
    { enemy: 'goliath', count: 3, delay: 1.8 },
    { enemy: 'haulerbot', count: 3, delay: 1.7 },
    { enemy: 'archon', count: 2, delay: 1.2 },
  ],
  cryostation: [
    { enemy: 'dashling', count: 14, delay: 0.3 },
    { enemy: 'veilguard', count: 5, delay: 1.0 },
    { enemy: 'sentrybot', count: 3, delay: 1.4 },
    { enemy: 'reaperbot', count: 1, delay: 1.5 },
  ],
  floodway: [
    { enemy: 'raider', count: 7, delay: 0.7 },
    { enemy: 'gunner', count: 7, delay: 0.7 },
    { enemy: 'nulltick', count: 4, delay: 1.1 },
    { enemy: 'warlord', count: 1, delay: 1.4 },
  ],
  verdigris: [
    { enemy: 'locust', count: 22, delay: 0.2 },
    { enemy: 'venomspitter', count: 5, delay: 0.9 },
    { enemy: 'hivecrawler', count: 3, delay: 1.8 },
    { enemy: 'matriarch', count: 1, delay: 1.5 },
  ],
  umbra: [
    { enemy: 'mechpilot', count: 2, delay: 1.4 },
    { enemy: 'reaperbot', count: 2, delay: 1.5 },
    { enemy: 'archon', count: 3, delay: 1.2 },
    { enemy: 'siegebreaker', count: 1, delay: 1.5 },
  ],
  aurora: [
    { enemy: 'dashling', count: 14, delay: 0.3 },
    { enemy: 'veilguard', count: 5, delay: 1.0 },
    { enemy: 'sentrybot', count: 3, delay: 1.4 },
    { enemy: 'archon', count: 1, delay: 1.0 },
  ],
  emberfall: [
    { enemy: 'locust', count: 22, delay: 0.22 },
    { enemy: 'goliath', count: 3, delay: 1.7 },
    { enemy: 'sporethrower', count: 4, delay: 1.0 },
    { enemy: 'colossus', count: 1, delay: 1.0 },
  ],
  tideline: [
    { enemy: 'scuttler', count: 20, delay: 0.25 },
    { enemy: 'raider', count: 6, delay: 0.8 },
    { enemy: 'nulltick', count: 3, delay: 1.1 },
    { enemy: 'leviathan', count: 1, delay: 1.5 },
  ],
};

/** Themed boss rotation per theatre; a boss lands on every 5th wave. */
const BOSS_POOL: Record<Theme, string[]> = {
  kappa: ['hivequeen', 'leviathan'],
  helios: ['siegebreaker', 'leviathan'],
  cinder: ['hivequeen', 'matriarch'],
  meridian: ['warlord', 'siegebreaker'],
  ashfall: ['warlord', 'matriarch'],
  theta: ['leviathan', 'siegebreaker', 'hivequeen', 'warlord', 'matriarch'],
  obsidian: ['hivequeen', 'leviathan'],
  cryostation: ['siegebreaker', 'leviathan'],
  floodway: ['warlord', 'siegebreaker'],
  verdigris: ['matriarch', 'hivequeen'],
  umbra: ['leviathan', 'warlord'],
  aurora: ['siegebreaker', 'leviathan'],
  emberfall: ['colossus', 'matriarch'],
  tideline: ['leviathan', 'warlord'],
};

/**
 * Extends a mission's authored waves up to `target` with escalating mixed
 * groups and a themed boss every fifth wave.
 */
function extendWaves(base: WaveDef[], theme: Theme, target = 30): WaveDef[] {
  const out: WaveDef[] = base.map((w) => ({ ...w, groups: w.groups.map((gr) => ({ ...gr })) }));
  const pool = FAMILY_POOL[theme];
  const bosses = BOSS_POOL[theme];
  let bossIdx = 0;

  for (let id = out.length + 1; id <= target; id++) {
    const ramp = id - out.length;
    const scale = 1 + ramp * 0.09;
    const groups: WaveGroup[] = [];
    const picks = 2 + Math.floor(ramp / 6);
    for (let k = 0; k < picks; k++) {
      const pick = pool[(id + k * 3) % pool.length];
      groups.push(
        g(
          pick.enemy,
          Math.max(1, Math.round(pick.count * (0.7 + scale * 0.5))),
          pick.delay,
          k * 1.5,
          k % 3,
        ),
      );
    }

    const isBoss = id % 5 === 0;
    let label = `SURGE ${id}`;
    if (isBoss) {
      const boss = bosses[bossIdx % bosses.length];
      bossIdx++;
      groups.push(g(boss, 1, 1, 2, id % 3));
      label = id === target ? `FINAL — ${boss.toUpperCase()}` : boss.toUpperCase();
    }
    out.push({ id, earlyBonus: 40 + id * 4, label, groups });
  }
  return out;
}

/** Kappa — 10 waves, tutorial curve. */
const WAVES_KAPPA_BASE: WaveDef[] = [
  { id: 1, earlyBonus: 20, label: 'SCOUT ELEMENT', groups: [g('scuttler', 6, 0.7, 0)] },
  { id: 2, earlyBonus: 22, label: 'INFILTRATION', groups: [g('scuttler', 8, 0.55, 0), g('husk', 2, 1.4, 1.5)] },
  { id: 3, earlyBonus: 25, label: 'SWARM PUSH', groups: [g('scuttler', 14, 0.35, 0), g('locust', 6, 0.3, 1.2), g('dashling', 3, 0.6, 2.5)] },
  { id: 4, earlyBonus: 28, label: 'PLATED CONTACT', groups: [g('husk', 6, 0.9, 0), g('plateback', 2, 1.6, 2)] },
  { id: 5, earlyBonus: 30, label: 'MIXED THREAT', groups: [g('scuttler', 10, 0.4, 0), g('dashling', 4, 0.7, 1.5), g('husk', 5, 1.1, 3), g('mender', 1, 1, 5)] },
  { id: 6, earlyBonus: 35, label: 'ELITE DETECTED', groups: [g('husk', 8, 0.8, 0), g('plateback', 2, 1.4, 2), g('archon', 1, 1, 6)] },
  { id: 7, earlyBonus: 40, label: 'SUPPORT INBOUND', groups: [g('veilguard', 3, 1.2, 0), g('scuttler', 12, 0.35, 1), g('broodnode', 1, 1, 5)] },
  { id: 8, earlyBonus: 45, label: 'HEAVY ASSAULT', groups: [g('goliath', 2, 2.2, 0), g('plateback', 3, 1.3, 2), g('husk', 8, 0.7, 4), g('nulltick', 2, 1.5, 8)] },
  { id: 9, earlyBonus: 50, label: 'BREACH ATTEMPT', groups: [g('dashling', 8, 0.4, 0), g('sporethrower', 3, 1.1, 2), g('archon', 1, 1, 5), g('goliath', 1, 1, 8), g('veilguard', 2, 1, 10)] },
  { id: 10, earlyBonus: 80, label: 'COLOSSUS', groups: [g('husk', 6, 0.8, 0), g('plateback', 2, 1.2, 3), g('archon', 1, 1, 6), g('colossus', 1, 1, 10)] },
];

/** Helios — 12 waves, energy/shield focus, runners. */
const WAVES_HELIOS_BASE: WaveDef[] = [
  { id: 1, earlyBonus: 22, label: 'COLD OPEN', groups: [g('scuttler', 10, 0.5, 0)] },
  { id: 2, earlyBonus: 24, label: 'ICE RUNNERS', groups: [g('dashling', 6, 0.55, 0), g('husk', 4, 0.9, 2)] },
  { id: 3, earlyBonus: 26, label: 'SHIELD PROBE', groups: [g('veilguard', 2, 1.3, 0), g('dronebot', 5, 0.7, 0.8), g('scuttler', 12, 0.4, 1)] },
  { id: 4, earlyBonus: 30, label: 'PLATED COLUMN', groups: [g('plateback', 3, 1.5, 0), g('husk', 6, 0.9, 2)] },
  { id: 5, earlyBonus: 32, label: 'SWARM UNDER ICE', groups: [g('scuttler', 20, 0.28, 0), g('dashling', 5, 0.5, 3)] },
  { id: 6, earlyBonus: 36, label: 'ELITE CONTACT', groups: [g('archon', 1, 1, 0), g('veilguard', 3, 1.1, 2), g('mender', 2, 1.4, 5)] },
  { id: 7, earlyBonus: 40, label: 'JAM FIELD', groups: [g('nulltick', 3, 1.2, 0), g('sentrybot', 2, 1.6, 0.6), g('husk', 8, 0.7, 1), g('sporethrower', 2, 1.3, 4)] },
  { id: 8, earlyBonus: 45, label: 'SIEGE FORMS', groups: [g('goliath', 2, 2.4, 0), g('plateback', 3, 1.4, 2), g('broodnode', 2, 2, 5)] },
  { id: 9, earlyBonus: 50, label: 'SHIELD WALL', groups: [g('veilguard', 5, 1.0, 0), g('archon', 1, 1, 4), g('scuttler', 14, 0.3, 6)] },
  { id: 10, earlyBonus: 55, label: 'BLACK ICE', groups: [g('dashling', 12, 0.35, 0), g('nulltick', 2, 1.2, 2), g('goliath', 2, 2, 5), g('archon', 1, 1, 8)] },
  { id: 11, earlyBonus: 60, label: 'OVERWHELM', groups: [g('scuttler', 24, 0.25, 0), g('plateback', 4, 1.2, 2), g('veilguard', 3, 1.1, 6), g('mender', 2, 1.3, 9)] },
  { id: 12, earlyBonus: 100, label: 'VAULT COLOSSUS', groups: [g('husk', 8, 0.7, 0), g('archon', 2, 1.2, 3), g('goliath', 2, 2, 6), g('colossus', 1, 1, 10)] },
];

/** Cinder — 12 waves, thermal-vulnerable swarms, tight early pressure. */
const WAVES_CINDER_BASE: WaveDef[] = [
  { id: 1, earlyBonus: 24, label: 'SLAG SWARM', groups: [g('scuttler', 12, 0.35, 0)] },
  { id: 2, earlyBonus: 26, label: 'HOT LANE', groups: [g('dashling', 6, 0.5, 0), g('locust', 10, 0.28, 1.0), g('scuttler', 10, 0.35, 1.5)] },
  { id: 3, earlyBonus: 28, label: 'BURNERS', groups: [g('mender', 2, 1.2, 0), g('husk', 6, 0.8, 1)] },
  { id: 4, earlyBonus: 32, label: 'ARMOUR WALK', groups: [g('plateback', 3, 1.4, 0), g('husk', 5, 0.9, 2)] },
  { id: 5, earlyBonus: 36, label: 'CALDERA SURGE', groups: [g('scuttler', 22, 0.25, 0), g('wasp', 8, 0.4, 1.5), g('dashling', 6, 0.45, 2), g('broodnode', 1, 1, 5)] },
  { id: 6, earlyBonus: 40, label: 'ELITE', groups: [g('archon', 1, 1, 0), g('plateback', 2, 1.3, 2), g('nulltick', 2, 1.2, 5)] },
  { id: 7, earlyBonus: 44, label: 'VENTING', groups: [g('venomspitter', 4, 1.0, 0), g('sporethrower', 3, 1.0, 1), g('veilguard', 3, 1.1, 2), g('husk', 8, 0.7, 4)] },
  { id: 8, earlyBonus: 50, label: 'HEAVY', groups: [g('goliath', 3, 2.0, 0), g('mender', 2, 1.3, 3), g('scuttler', 16, 0.3, 6)] },
  { id: 9, earlyBonus: 55, label: 'TWIN ELITES', groups: [g('archon', 2, 1.4, 0), g('veilguard', 3, 1.1, 3), g('dashling', 8, 0.4, 6)] },
  { id: 10, earlyBonus: 60, label: 'SIEGE', groups: [g('hivecrawler', 2, 2.2, 0), g('goliath', 3, 1.8, 1), g('plateback', 4, 1.2, 2), g('nulltick', 2, 1.2, 9)] },
  { id: 11, earlyBonus: 70, label: 'MAGMA PUSH', groups: [g('scuttler', 28, 0.22, 0), g('archon', 2, 1.2, 4), g('goliath', 2, 2, 8)] },
  { id: 12, earlyBonus: 110, label: 'CINDER COLOSSUS', groups: [g('plateback', 4, 1.2, 0), g('veilguard', 3, 1, 3), g('archon', 1, 1, 6), g('colossus', 1, 1, 9)] },
];

/** Meridian — 14 waves, multi-threat, jam + ranged. */
const WAVES_MERIDIAN_BASE: WaveDef[] = [
  { id: 1, earlyBonus: 24, label: 'PERIMETER', groups: [g('scuttler', 10, 0.4, 0), g('husk', 3, 1.0, 2)] },
  { id: 2, earlyBonus: 26, label: 'CLIMBERS', groups: [g('dashling', 8, 0.4, 0)] },
  { id: 3, earlyBonus: 30, label: 'SHIELD ASCENT', groups: [g('veilguard', 3, 1.2, 0), g('scuttler', 12, 0.35, 1)] },
  { id: 4, earlyBonus: 34, label: 'PLATED', groups: [g('plateback', 4, 1.3, 0), g('husk', 6, 0.85, 2)] },
  { id: 5, earlyBonus: 38, label: 'SWARM LIFT', groups: [g('scuttler', 24, 0.25, 0), g('dashling', 6, 0.4, 3)] },
  { id: 6, earlyBonus: 42, label: 'ELITE PILOT', groups: [g('gunner', 5, 0.8, 0), g('archon', 1, 1, 1.5), g('nulltick', 2, 1.1, 2), g('mender', 2, 1.2, 5)] },
  { id: 7, earlyBonus: 46, label: 'SPORE BATTERY', groups: [g('raider', 5, 0.9, 0), g('sporethrower', 4, 0.9, 1), g('veilguard', 3, 1.1, 3)] },
  { id: 8, earlyBonus: 52, label: 'BROODLINE', groups: [g('broodnode', 3, 1.8, 0), g('scuttler', 16, 0.3, 2), g('husk', 8, 0.7, 5)] },
  { id: 9, earlyBonus: 58, label: 'SIEGE COLUMN', groups: [g('sharpshooter', 4, 1.0, 0), g('goliath', 3, 2.0, 1), g('plateback', 4, 1.2, 3), g('archon', 1, 1, 7)] },
  { id: 10, earlyBonus: 64, label: 'JAM STORM', groups: [g('nulltick', 4, 1.0, 0), g('dashling', 10, 0.35, 2), g('veilguard', 4, 1.0, 5)] },
  { id: 11, earlyBonus: 70, label: 'TWIN ELITES', groups: [g('archon', 2, 1.3, 0), g('goliath', 2, 2.0, 4), g('sporethrower', 3, 1.1, 7)] },
  { id: 12, earlyBonus: 80, label: 'MASS SWARM', groups: [g('scuttler', 32, 0.2, 0), g('plateback', 4, 1.15, 3), g('archon', 1, 1, 8)] },
  { id: 13, earlyBonus: 90, label: 'HARDENED', groups: [g('goliath', 4, 1.8, 0), g('veilguard', 4, 1.0, 2), g('nulltick', 3, 1.1, 6), g('archon', 2, 1.2, 10)] },
  { id: 14, earlyBonus: 130, label: 'SPIRE COLOSSUS', groups: [g('husk', 10, 0.6, 0), g('plateback', 3, 1.2, 3), g('archon', 2, 1.2, 6), g('colossus', 1, 1, 10)] },
];

/** Ashfall — 14 waves, elite-heavy urban hell. */
const WAVES_ASHFALL_BASE: WaveDef[] = [
  { id: 1, earlyBonus: 26, label: 'STREET SWEEP', groups: [g('scuttler', 12, 0.35, 0), g('dashling', 4, 0.5, 2)] },
  { id: 2, earlyBonus: 30, label: 'BLOCKADE', groups: [g('husk', 8, 0.7, 0), g('plateback', 2, 1.4, 2)] },
  { id: 3, earlyBonus: 34, label: 'SHIELD LINE', groups: [g('veilguard', 4, 1.1, 0), g('scuttler', 14, 0.3, 1)] },
  { id: 4, earlyBonus: 38, label: 'FIRST ELITE', groups: [g('archon', 1, 1, 0), g('mender', 2, 1.2, 2), g('husk', 6, 0.8, 4)] },
  { id: 5, earlyBonus: 42, label: 'RUSH HOUR', groups: [g('dashling', 14, 0.3, 0), g('scuttler', 18, 0.25, 1)] },
  { id: 6, earlyBonus: 48, label: 'HEAVY ARMOUR', groups: [g('haulerbot', 2, 2.0, 0), g('plateback', 4, 1.25, 1), g('goliath', 1, 2.2, 3), g('nulltick', 2, 1.2, 6)] },
  { id: 7, earlyBonus: 54, label: 'RANGED HELL', groups: [g('sporethrower', 5, 0.9, 0), g('veilguard', 3, 1.0, 3), g('broodnode', 2, 1.8, 6)] },
  { id: 8, earlyBonus: 60, label: 'DUAL ELITE', groups: [g('reaperbot', 1, 1, 0), g('archon', 1, 1.3, 1.5), g('plateback', 3, 1.2, 3), g('dashling', 8, 0.35, 6)] },
  { id: 9, earlyBonus: 66, label: 'GRINDER', groups: [g('scuttler', 30, 0.2, 0), g('goliath', 2, 2.0, 3), g('mender', 3, 1.2, 6)] },
  { id: 10, earlyBonus: 74, label: 'JAM + SHIELD', groups: [g('nulltick', 3, 1.1, 0), g('veilguard', 5, 1.0, 1), g('archon', 1, 1, 5)] },
  { id: 11, earlyBonus: 82, label: 'SIEGE', groups: [g('mechpilot', 1, 1, 0), g('goliath', 3, 1.7, 1.5), g('plateback', 4, 1.15, 2), g('gunner', 5, 0.8, 6)] },
  { id: 12, earlyBonus: 90, label: 'TRIPLE THREAT', groups: [g('archon', 3, 1.2, 0), g('nulltick', 3, 1.0, 3), g('dashling', 12, 0.3, 6)] },
  { id: 13, earlyBonus: 100, label: 'LAST STAND', groups: [g('scuttler', 36, 0.18, 0), g('plateback', 5, 1.1, 2), g('goliath', 3, 1.8, 6), g('archon', 2, 1.2, 10)] },
  { id: 14, earlyBonus: 150, label: 'ASHFALL COLOSSUS', groups: [g('veilguard', 4, 1.0, 0), g('archon', 2, 1.2, 3), g('goliath', 2, 1.8, 6), g('colossus', 1, 1, 9)] },
];

/** Theta — 12 waves, short lane, brutal. Double colossus finish. */
const WAVES_THETA_BASE: WaveDef[] = [
  { id: 1, earlyBonus: 28, label: 'CORE ALARM', groups: [g('scuttler', 14, 0.3, 0), g('husk', 4, 0.8, 1)] },
  { id: 2, earlyBonus: 32, label: 'FAST BREACH', groups: [g('dashling', 10, 0.35, 0)] },
  { id: 3, earlyBonus: 36, label: 'PLATED', groups: [g('plateback', 4, 1.2, 0), g('veilguard', 2, 1.1, 2)] },
  { id: 4, earlyBonus: 42, label: 'ELITE', groups: [g('archon', 1, 1, 0), g('goliath', 1, 2, 2), g('mender', 2, 1.2, 4)] },
  { id: 5, earlyBonus: 48, label: 'SWARM FLOOD', groups: [g('scuttler', 28, 0.2, 0), g('dashling', 8, 0.35, 2), g('broodnode', 2, 1.6, 4)] },
  { id: 6, earlyBonus: 56, label: 'JAM LINE', groups: [g('nulltick', 3, 1.0, 0), g('plateback', 3, 1.2, 2), g('sporethrower', 3, 1.0, 4)] },
  { id: 7, earlyBonus: 64, label: 'HEAVY', groups: [g('goliath', 3, 1.8, 0), g('veilguard', 4, 1.0, 2), g('archon', 1, 1, 5)] },
  { id: 8, earlyBonus: 72, label: 'DUAL ELITE', groups: [g('reaperbot', 1, 1, 0), g('archon', 1, 1.2, 1.2), g('plateback', 4, 1.15, 2), g('mender', 2, 1.2, 5)] },
  { id: 9, earlyBonus: 80, label: 'CRUSH', groups: [g('scuttler', 32, 0.18, 0), g('goliath', 3, 1.7, 3), g('nulltick', 3, 1.0, 6)] },
  { id: 10, earlyBonus: 90, label: 'PRE-BOSS', groups: [g('mechpilot', 1, 1, 0), g('archon', 2, 1.15, 1.2), g('veilguard', 4, 1.0, 2), g('goliath', 2, 1.8, 5)] },
  { id: 11, earlyBonus: 100, label: 'BOSS SHIELD', groups: [g('plateback', 5, 1.1, 0), g('sporethrower', 4, 0.9, 2), g('archon', 2, 1.2, 5), g('colossus', 1, 1, 8)] },
  { id: 12, earlyBonus: 180, label: 'THETA FALL', groups: [g('archon', 3, 1.1, 0), g('goliath', 3, 1.6, 3), g('colossus', 1, 1, 7), g('nulltick', 2, 1, 12)] },
];

function makeMultiLaneWaves(theme: Theme, boss: string, label: string): WaveDef[] {
  const base: WaveDef[] = [
    {
      id: 1,
      earlyBonus: 30,
      label: 'THREE-WAY CONTACT',
      groups: [g('scuttler', 7, 0.45, 0, 0), g('scuttler', 7, 0.45, 0.35, 1), g('scuttler', 7, 0.45, 0.7, 2)],
    },
    {
      id: 2,
      earlyBonus: 34,
      label: 'SPLIT ASSAULT',
      groups: [g('husk', 4, 0.9, 0, 0), g('dashling', 8, 0.35, 0.4, 1), g('plateback', 2, 1.4, 1, 2)],
    },
    {
      id: 3,
      earlyBonus: 38,
      label: 'MERGE VECTOR',
      groups: [g('dashling', 9, 0.35, 0, 0), g('veilguard', 3, 1.2, 0.6, 1), g('scuttler', 14, 0.3, 1.2, 2)],
    },
    {
      id: 4,
      earlyBonus: 44,
      label: 'HEAVY CROSSCURRENT',
      groups: [g('plateback', 3, 1.2, 0, 0), g('goliath', 2, 1.8, 1, 1), g('nulltick', 3, 1.0, 2, 2)],
    },
    {
      id: 5,
      earlyBonus: 52,
      label: 'ELITE ON THE RIDGE',
      groups: [g('archon', 1, 1, 0, 0), g('mender', 2, 1.2, 1.5, 1), g('sporethrower', 3, 1.0, 2.5, 2)],
    },
    {
      id: 6,
      earlyBonus: 60,
      label: 'TRIPLE SWARM',
      groups: [g('scuttler', 18, 0.25, 0, 0), g('locust', 12, 0.25, 0.5, 1), g('dashling', 10, 0.3, 1, 2)],
    },
    {
      id: 7,
      earlyBonus: 72,
      label: 'FORTIFICATION TEST',
      groups: [g('goliath', 2, 1.7, 0, 0), g('plateback', 4, 1.1, 1, 1), g('veilguard', 4, 1.0, 2, 2)],
    },
    {
      id: 8,
      earlyBonus: 110,
      label,
      groups: [g('archon', 1, 1.2, 0, 0), g('nulltick', 2, 1.0, 1.5, 1), g(boss, 1, 1, 3, 2)],
    },
  ];
  return extendWaves(base, theme);
}

export const WAVES_KAPPA = extendWaves(WAVES_KAPPA_BASE, 'kappa');
export const WAVES_HELIOS = extendWaves(WAVES_HELIOS_BASE, 'helios');
export const WAVES_CINDER = extendWaves(WAVES_CINDER_BASE, 'cinder');
export const WAVES_MERIDIAN = extendWaves(WAVES_MERIDIAN_BASE, 'meridian');
export const WAVES_ASHFALL = extendWaves(WAVES_ASHFALL_BASE, 'ashfall');
export const WAVES_THETA = extendWaves(WAVES_THETA_BASE, 'theta');
export const WAVES_OBSIDIAN = makeMultiLaneWaves('obsidian', 'hivequeen', 'FOUNDRY BREACH');
export const WAVES_CRYOSTATION = makeMultiLaneWaves('cryostation', 'siegebreaker', 'NADIR COLLAPSE');
export const WAVES_FLOODWAY = makeMultiLaneWaves('floodway', 'warlord', 'QUAY OVERRUN');
export const WAVES_VERDIGRIS = makeMultiLaneWaves('verdigris', 'matriarch', 'BLOOM HEART');
export const WAVES_UMBRA = makeMultiLaneWaves('umbra', 'leviathan', 'LIFT FAILURE');
export const WAVES_AURORA = makeMultiLaneWaves('aurora', 'siegebreaker', 'CITADEL BREAK');
export const WAVES_EMBERFALL = extendWaves([
  { id: 1, earlyBonus: 32, label: 'FURNACE WAKE', groups: [g('locust', 16, 0.28, 0)] },
  { id: 2, earlyBonus: 36, label: 'TRENCH RUN', groups: [g('dashling', 10, 0.35, 0), g('husk', 5, 0.8, 2)] },
  { id: 3, earlyBonus: 40, label: 'ASH CLOUD', groups: [g('sporethrower', 3, 1.0, 0), g('mender', 2, 1.2, 3), g('plateback', 2, 1.4, 5)] },
  { id: 4, earlyBonus: 46, label: 'BASALT WALL', groups: [g('goliath', 2, 1.8, 0), g('nulltick', 2, 1.1, 3), g('locust', 18, 0.25, 6)] },
  { id: 5, earlyBonus: 56, label: 'VAULT HEART', groups: [g('archon', 1, 1.1, 0), g('plateback', 3, 1.2, 2), g('colossus', 1, 1.0, 7)] },
], 'emberfall');
export const WAVES_TIDELINE = makeMultiLaneWaves('tideline', 'leviathan', 'ARRAY OVERRUN');
export const MISSION_WAVES: Record<string, WaveDef[]> = {
  kappa: WAVES_KAPPA,
  helios: WAVES_HELIOS,
  cinder: WAVES_CINDER,
  meridian: WAVES_MERIDIAN,
  ashfall: WAVES_ASHFALL,
  theta: WAVES_THETA,
  obsidian: WAVES_OBSIDIAN,
  cryostation: WAVES_CRYOSTATION,
  floodway: WAVES_FLOODWAY,
  verdigris: WAVES_VERDIGRIS,
  umbra: WAVES_UMBRA,
  aurora: WAVES_AURORA,
  emberfall: WAVES_EMBERFALL,
  tideline: WAVES_TIDELINE,
};



